import { zodResolver } from "@hookform/resolvers/zod";
import { HTTPError } from "@marketplace/http";
import { notifications } from "@marketplace/ui/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { CreateBclProductRequestDto } from "../../dtos/bcl-product-dto";
import { ProductQueryKeys } from "../../generators/product-query-keys";
import { usePostCreateBclProductMutation } from "../mutations/use-post-create-bcl-product-mutation";
import { usePutUpdateBclProductMutation } from "../mutations/use-put-update-bcl-product-mutation";
import { useGetBclProductDetailQuery } from "../queries/use-get-bcl-product-detail-query";
import {
  type BclProductFormSchema,
  useBclProductFormSchema
} from "../schemas/use-bcl-product-form-schema";

type UseBclProductFormProps = {
  productId: string;
  onSuccess?: () => void;
};

type UseBclProductFormReturn = {
  formMethods: UseFormReturn<BclProductFormSchema>;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  isPending: boolean;
  isLoadingDetail: boolean;
};

export const useBclProductForm = ({
  productId,
  onSuccess
}: UseBclProductFormProps): UseBclProductFormReturn => {
  const { t } = useTranslation("product");
  const queryClient = useQueryClient();
  const schema = useBclProductFormSchema();

  const formMethods = useForm<BclProductFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      product_id: ""
    }
  });

  const {
    data: detailData,
    isLoading: isLoadingDetail,
    error: detailError
  } = useGetBclProductDetailQuery({
    productId,
    options: {
      enabled: !!productId,
      retry: false
    }
  });

  const isExisting = !!detailData?.data && !detailError;

  // Populate form with existing data
  useEffect(() => {
    if (detailData?.data && !detailError) {
      formMethods.reset({
        product_id: detailData.data.product_id
      });
    }
  }, [detailData, detailError, formMethods]);

  // Clear form on 404
  useEffect(() => {
    if (detailError && detailError instanceof HTTPError) {
      if (detailError.response.status === 404) {
        formMethods.reset({
          product_id: ""
        });
      }
    }
  }, [detailError, formMethods]);

  const createMutation = usePostCreateBclProductMutation({
    options: {
      onSuccess: () => {
        notifications.show({
          message: t(
            "integrationProductListPage.bclModal.notifications.successCreate"
          )
        });
        queryClient.invalidateQueries({
          queryKey: ProductQueryKeys._def
        });
        formMethods.reset();
        onSuccess?.();
      },
      onError: (error) => {
        if (error instanceof HTTPError) {
          notifications.show({
            variant: "error",
            message: t(
              "integrationProductListPage.bclModal.notifications.failedToCreate"
            )
          });
        }
      }
    }
  });

  const updateMutation = usePutUpdateBclProductMutation({
    options: {
      onSuccess: () => {
        notifications.show({
          message: t(
            "integrationProductListPage.bclModal.notifications.successUpdate"
          )
        });
        queryClient.invalidateQueries({
          queryKey: ProductQueryKeys._def
        });
        formMethods.reset();
        onSuccess?.();
      },
      onError: (error) => {
        if (error instanceof HTTPError) {
          notifications.show({
            variant: "error",
            message: t(
              "integrationProductListPage.bclModal.notifications.failedToUpdate"
            )
          });
        }
      }
    }
  });

  const onSubmit = formMethods.handleSubmit(async (data) => {
    const payload: CreateBclProductRequestDto = {
      lampu_product_id: productId,
      product_id: data.product_id
    };

    if (isExisting) {
      await updateMutation.mutateAsync({
        ...payload,
        productId
      });
    } else {
      await createMutation.mutateAsync(payload);
    }
  });

  return {
    formMethods,
    onSubmit,
    isPending: createMutation.isPending || updateMutation.isPending,
    isLoadingDetail
  };
};
